import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { TERMS_VERSION } from '@/lib/affiliate';

interface Section {
  n: string;
  title: string;
  paras?: string[];
  bullets?: string[];
}

const SECTIONS: Section[] = [
  {
    n: '1',
    title: 'Program Overview',
    paras: [
      'This Affiliate Referral Program (the "Program") is operated by GRID LLC, a Wyoming, USA limited liability company ("GRID", "we", "us"). It lets approved participants ("Affiliates", "you") earn a commission when a business or contact you refer becomes paying work for GRID. By creating an affiliate code or otherwise participating, you agree to this Agreement.',
    ],
  },
  {
    n: '2',
    title: 'Eligibility and Affiliate Approval',
    paras: [
      'You must be at least 18 years old, have legal capacity to enter contracts, and not be barred from receiving payments under applicable law or sanctions/export rules. GRID may approve or decline any applicant, and may suspend or remove any Affiliate, at its discretion.',
    ],
  },
  {
    n: '3',
    title: 'Referral Submission, Acceptance, and Attribution',
    paras: [
      'A referral must be submitted to GRID and accepted by GRID before commission eligibility begins. If multiple Affiliates refer the same prospect, the first accepted referral with documented prospect consent receives credit, unless GRID determines another Affiliate materially drove the sale.',
      'Approved referrals remain eligible for commission for 180 days from acceptance, unless the prospect was already in active discussions with GRID at the time of submission. GRID determines tier eligibility, qualification, and attribution in good faith, and its determination is final.',
    ],
  },
  {
    n: '4',
    title: 'Commission Tiers (One-Time Project Work)',
    paras: [
      'For one-time project work, your commission is the single highest referral tier your involvement reaches: Warm Introduction 10%, Qualified Referral 15%, or Active Closing Partner 20%. These three tiers do not stack.',
      'Commissionable revenue means the project or service revenue actually collected by GRID, excluding taxes, refunds, chargebacks, payment-processing fees, software and license costs, hosting and other pass-through costs, ad spend, blockchain gas/network fees, subcontractor pass-through costs, and other third-party expenses. It does not exclude GRID’s normal internal labor. There is no payout cap.',
    ],
  },
  {
    n: '5',
    title: 'Recurring Commission',
    paras: [
      'If your referred client signs an ongoing/recurring plan (maintenance, support, hosting, audits, or continued development), you earn 5% per month for up to 12 months — in addition to any one-time project commission — calculated only on the recurring monthly service revenue GRID actually collects. Recurring commission applies only to approved ongoing service plans.',
    ],
  },
  {
    n: '6',
    title: 'Bonuses',
    paras: [
      'GRID may pay the following bonuses on top of base commission, on collected revenue and subject to the same payment, refund, and conduct rules:',
    ],
    bullets: [
      'Large Deal Bonus — on a deal you close as an Active Closing Partner, an extra 2% on collected project revenue above $25,000.',
      'Fast Start Bonus — a one-time $250 bonus on your first closed referral, for deals over $2,500.',
    ],
  },
  {
    n: '7',
    title: 'Payment Timing',
    paras: [
      'Commission is earned and payable only after GRID has received the client’s payment, calculated on collected revenue — not quoted, estimated, or invoiced amounts. Payouts are made within 30 days of GRID receiving the client’s payment. There is no minimum payout threshold.',
    ],
  },
  {
    n: '8',
    title: 'RLUSD Payout Rules',
    paras: [
      'Commissions are calculated in USD and paid in RLUSD, a US-dollar-pegged stablecoin issued on the XRP Ledger, at the time GRID processes the payout. RLUSD is issued by a third party and is intended to maintain a 1:1 value with the US dollar; GRID does not guarantee its value, its peg, or its availability, and is not responsible for exchange issues or network conditions.',
      'You are responsible for providing a valid XRPL wallet address configured with an RLUSD trustline so it can receive RLUSD. Payments sent to an incorrect wallet address provided by you are considered paid.',
    ],
  },
  {
    n: '9',
    title: 'Taxes and Independent Contractor Status',
    paras: [
      'You are an independent contractor, not an employee, agent, partner, or joint venturer of GRID, and are responsible for your own taxes, reporting, and compliance. Income from digital assets is taxable.',
      'You may be required to provide tax documentation before receiving payment — typically a W-9 for U.S. persons or the applicable W-8BEN / W-8BEN-E for non-U.S. persons. GRID may withhold payment until valid forms are provided and may issue tax forms (e.g. 1099-NEC) as required by law.',
    ],
  },
  {
    n: '10',
    title: 'Refunds, Cancellations, and Chargebacks',
    paras: [
      'If GRID refunds, credits, reverses, or writes off client revenue, no commission is owed on that amount. If a commission was already paid on revenue that is later refunded, charged back, or written off, GRID may offset that amount against your future commissions.',
    ],
  },
  {
    n: '11',
    title: 'Consent, Anti-Spam, and Disclosure',
    paras: [
      'GRID does not engage in cold outreach. You may only refer prospects who have consented to be contacted by GRID or who directly request contact, and you must be able to show proof of consent if asked.',
      'You must comply with all applicable laws, including the CAN-SPAM Act, the Telephone Consumer Protection Act (TCPA), and all anti-spam, telemarketing, and data-protection laws. You must not send spam or unsolicited marketing calls, texts, or emails.',
      'You must clearly disclose when you may receive compensation for referring business to GRID — including in public posts, videos, newsletters, websites, and direct promotional messages where applicable. A simple disclosure is acceptable, for example: "I may earn a referral commission if you become a GRID client."',
    ],
  },
  {
    n: '12',
    title: 'Prohibited Conduct',
    paras: ['Affiliates may not:'],
    bullets: [
      'Use spam, fake accounts, bots, misleading claims, or pressure tactics.',
      'Misrepresent GRID’s services, pricing, capabilities, or partnerships.',
      'Use GRID branding in paid ads without written approval, or bid on GRID trademarks in paid search.',
      'Refer illegal, fraudulent, or high-risk projects.',
      'Promise investment returns, token profits, or guaranteed blockchain outcomes.',
      'Send unsolicited marketing messages without proper consent.',
      'Self-refer, submit fake or duplicate leads, or otherwise manipulate or circumvent the Program.',
    ],
  },
  {
    n: '13',
    title: 'GRID Control Over Pricing, Scope, and Client Acceptance',
    paras: [
      'You may not bind GRID, quote final pricing, promise discounts, guarantee outcomes or delivery dates, or make commitments on GRID’s behalf unless authorized in writing. GRID has sole discretion to accept, reject, scope, price, or terminate any client engagement.',
    ],
  },
  {
    n: '14',
    title: 'Confidentiality',
    paras: [
      'Client information, deal details, pricing, and other non-public information you learn through the Program are confidential. You will not disclose or use them except to perform under this Agreement.',
    ],
  },
  {
    n: '15',
    title: 'No Earnings Guarantee',
    paras: [
      'The Program creates no guarantee of any earnings. Example payouts shown in Program materials are illustrative only, based on stated assumptions, and are not typical, promised, or guaranteed results. Actual earnings depend on the deals you refer and what GRID collects.',
    ],
  },
  {
    n: '16',
    title: 'Disclaimers; Limitation of Liability',
    paras: [
      'The Program is provided "as is." To the maximum extent permitted by law, GRID disclaims all implied warranties and will not be liable for indirect, incidental, special, or consequential damages, or for lost profits. GRID’s total liability under this Agreement will not exceed the total commissions paid to you in the 12 months before the event giving rise to the claim.',
    ],
  },
  {
    n: '17',
    title: 'Indemnification',
    paras: [
      'You will indemnify and hold harmless GRID and its owners, officers, and contractors from claims, losses, and expenses (including reasonable legal fees) arising from your participation, your marketing conduct, your breach of this Agreement, or your violation of any law or third-party right.',
    ],
  },
  {
    n: '18',
    title: 'Changes; Term; Termination',
    paras: [
      'GRID may modify the Program, including commission rates, tiers, bonuses, and these terms, on a going-forward basis, and may pause or end the Program at any time. Material changes will be posted with an updated version/date; continued participation after a change means you accept it.',
      'Either party may terminate participation at any time. Commissions properly earned before termination for deals GRID has already collected remain payable, subject to the refund, fraud, and compliance terms above. Sections intended to survive (taxes, confidentiality, liability, indemnification, governing law) survive termination.',
    ],
  },
  {
    n: '19',
    title: 'Governing Law; Disputes',
    paras: [
      'This Agreement is governed by the laws of the State of Wyoming, USA, without regard to its conflict-of-laws rules.',
      'Any dispute, claim, or controversy arising out of or relating to this Agreement or the Program that cannot be resolved informally will be settled by binding arbitration administered by the American Arbitration Association (AAA) under its Commercial Arbitration Rules. The arbitration will be seated in Wyoming, conducted in English, and may be held by video or telephone; the arbitrator’s award is final and may be entered in any court of competent jurisdiction. The Federal Arbitration Act governs the interpretation and enforcement of this section.',
      'Disputes will be resolved only on an individual basis. You and GRID waive any right to participate in a class, collective, or representative action.',
      'Exceptions: either party may bring an individual claim in small-claims court, and either party may seek injunctive or equitable relief in court to protect intellectual property or confidential information. Each party bears its own fees and costs except as the AAA rules or the arbitrator provide.',
    ],
  },
  {
    n: '20',
    title: 'Privacy',
    paras: [
      'GRID handles personal data in accordance with its Privacy Policy. You represent that any contact information you share was obtained lawfully and with the necessary consent.',
    ],
  },
  {
    n: '21',
    title: 'Entire Agreement',
    paras: [
      'This Agreement, together with the Program page descriptions referenced herein, is the entire agreement between you and GRID regarding the Program and supersedes prior understandings. If any provision is unenforceable, the rest remains in effect.',
    ],
  },
];

export function AffiliateTermsPage() {
  return (
    <Layout>
      <div style={{ maxWidth: '760px', margin: '0 auto' }}>
        <Link to="/affiliate-program" style={{ color: 'var(--text-3)', textDecoration: 'none', fontFamily: 'var(--font-mono)', fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          ← Back to Program
        </Link>

        <div className="terminal-card" style={{ marginTop: '16px' }}>
          <div className="card-header">
            <span className="card-num">TC</span>
            <span className="card-title">Affiliate Program Agreement</span>
          </div>
          <div className="card-body">
            <p className="prose prose--sm prose--muted">Version {TERMS_VERSION} · Last updated 2026-06-19</p>

            {SECTIONS.map((s) => (
              <div key={s.n} style={{ marginTop: '6px' }}>
                <div style={{ color: 'var(--accent)', fontWeight: 700, fontSize: '13px', marginBottom: '4px' }}>
                  {s.n}. {s.title}
                </div>
                {s.paras?.map((p, i) => (
                  <p key={i} className="prose prose--sm prose--muted">{p}</p>
                ))}
                {s.bullets && (
                  <ul className="rules-list" style={{ marginTop: '4px' }}>
                    {s.bullets.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}

            <p className="prose prose--sm" style={{ marginTop: '12px', color: 'var(--text)' }}>
              <strong>Contact:</strong> admin@gridservices.xyz
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
